import React from 'react'

import { DateTime } from 'luxon'

import { PERIOD_OPTIONS } from '@/components/charts/history/periodOptions'
import type { HistoryWindow } from '@/components/charts/history/useHistoryNavigation'
import { Alert } from '@/components/ui'
import { useApi } from '@/hooks'
import useCompany from '@/hooks/useCompany'
import { dataSourceParam } from '@/insights/common/dataSourceParam'
import Caption, { addDateToCaptionFields } from '@/insights/components/Caption'
import {
  type ApiService,
  type Asset,
  AssetTypeFilter,
  type InsightDefinition,
  InsightStatusCode,
  isInsightStatus,
} from '@/types/dashboard-types'

import AssetTimelineChart from './AssetTimelineChart'

import type { HistoryGraphParams } from '../historyGraph/HistoryGraph'
import { load } from '../historyGraph/HistoryGraph'
import type { AssetHistory } from '../historyGraph/types'

const DateOptions = [
  { label: 'Today', value: 'today' },
  { label: 'Yesterday', value: 'yesterday' },
  { label: 'Start of This Week', value: 'thisWeek' },
  { label: 'Start of Last Week', value: 'lastWeek' },
  { label: 'Start of This Month', value: 'thisMonth' },
  { label: 'Start of Last Month', value: 'previous1Month' },
  { label: 'Custom', value: 'custom' },
]

const AssetTimeline: InsightDefinition<HistoryGraphParams, AssetHistory> = {
  active: true,
  slug: 'asset/asset-timeline',
  category: 'asset',
  name: 'Asset Timeline',
  description: 'Gantt-style timeline view of asset state history',
  defaultWidth: 'full',
  defaultAspectRatio: 16 / 9,

  configuration: {
    controls: [
      { id: 'companyId', control: 'CompanyDropdown', required: true },
      { id: 'assetId', control: 'AssetDropdownSingle', required: true, assetType: AssetTypeFilter.InTransit },
      {
        id: 'date',
        control: 'CustomSingleSelect',
        options: DateOptions,
      },
      {
        id: 'from',
        label: 'Date',
        control: 'DateSelect',
        dependsOn: 'date',
        dependsOnValue: 'custom',
      },
      {
        id: 'periodHours',
        control: 'CustomSingleSelect',
        options: PERIOD_OPTIONS.map((o) => ({
          ...o,
          isDefault: o.value === '24',
        })),
      },
    ],

    isValid: (configuration: HistoryGraphParams) => {
      if (configuration?.companyId && configuration.assetId) {
        return true
      }
      return 'Company and Asset are required.'
    },
  },

  load,

  Display: ({ status, data, configuration }) => {
    if (status.code === InsightStatusCode.NOT_CONFIGURED) {
      return <Alert type="info" message={status.message || 'Insight has not been configured'} />
    }
    if (status.code !== InsightStatusCode.DISPLAYING) {
      return null
    }
    if (isInsightStatus(data) || !data) {
      return null
    }
    const params = configuration.parameters as HistoryGraphParams
    const from = DateTime.fromISO(data.from)
    const periodHours = parseInt(params.periodHours || '24', 10) || 24
    const reload = async (api: ApiService, window: HistoryWindow): Promise<AssetHistory | undefined> => {
      const response = await load(api, { ...params, date: 'custom', from: window.from.toISO()! })
      if (isInsightStatus(response)) {
        return undefined
      }
      return response
    }
    return (
      <section className="h-full w-full">
        <AssetTimelineChart data={data} from={from} periodHours={periodHours} reload={reload} />
      </section>
    )
  },

  Caption: ({ configuration, status }) => {
    const [asset, setAsset] = React.useState<Asset>()
    const { companies } = useCompany()
    const { api } = useApi()
    const params = configuration.parameters as HistoryGraphParams
    React.useEffect(() => {
      if (!api) return
      const fn = async () => {
        if (params.assetId) {
          const response = await api.get<Asset[]>({
            path: `/assets?companyId=${params.companyId}&id=${params.assetId}&deleted=false${dataSourceParam(params)}`,
            origin: 'AssetTimeline#caption',
          })
          if (response.data?.length) {
            setAsset(response.data[0])
          } else {
            setAsset({ id: Number(params.assetId) } as Asset)
          }
        }
      }
      fn()
    }, [params])

    if (status.code === InsightStatusCode.ERROR) {
      return ''
    }
    const fields: Record<string, string> = {}
    if (params.companyId) {
      const company = companies.find((c) => c.id === params.companyId)
      if (company) fields['Company'] = company.name
    }
    if (asset) fields['Asset'] = asset.name
    addDateToCaptionFields({ ...params, fields, finite: true })
    if (Object.keys(fields).length) return <Caption fields={fields} />
    return ''
  },

  Footer: ({ data, status }) => {
    if (status.code === InsightStatusCode.LOADING || status.code === InsightStatusCode.ERROR) {
      return ''
    }
    if (status.code === InsightStatusCode.NO_DATA || !data) {
      return <div>No data found</div>
    }
    const zoneCount = Object.keys(data.zones ?? {}).length
    const sampleCount = (data.ambient?.length ?? 0) + (data.movement?.length ?? 0)
    return (
      <div>
        {zoneCount} zone{zoneCount === 1 ? '' : 's'}, {sampleCount} samples
      </div>
    )
  },
}

export default AssetTimeline
